import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import add from 'date-fns/add';
import format from 'date-fns/format';
import isValid from 'date-fns/isValid';
import parse from 'date-fns/parse';
import { DateValidationPattern, FormatDateString, GetDateErrorType } from '../../date-range.utilities';

@Component({
  standalone: false,
  selector: 'adspt-date-range-input',
  templateUrl: './date-range-input.component.html',
  styleUrls: ['./date-range-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DateRangeInputComponent implements OnInit, OnChanges {
  readonly errorTexts = this.translate.instant('global-filter.date-selector.errors');
  @ViewChild('input') inputElement: ElementRef<HTMLInputElement>;

  @Input() selected = false;
  @Input() date: Date;
  @Input() minDate?: Date | null;
  @Input() maxDate?: Date | null;
  @Input() tooltipPlacement = 'top';
  @Output() dateChange: EventEmitter<Date> = new EventEmitter<Date>();

  tooltip: string;
  hasError = false;
  dateFormat = 'dd/MM/yyyy';

  onKeypressEvent(evt) {
    if (evt.key !== '/' && isNaN(+evt.key)) {
      // dont allow other chars
      evt.preventDefault();
    } else if (evt.key === '/') {
      const { value } = evt.target;
      if (!value || value.slice(-1) === '/' || value.split('/').length > 2) {
        // dis allow when '/' as 1st char or 2 consecutive '/' or more than 2 '/'
        evt.preventDefault();
      }
    }
  }

  onKeyupEvent(evt) {
    if ([37, 38, 39, 40].includes(evt.keyCode)) return;

    if (evt.target.value.length !== 10) {
      this.tooltip = this.errorTexts.default;
      this.hasError = true;
      if (evt.key === '/') {
        const newValue = FormatDateString(evt.target.value);

        evt.target.value = newValue;

        if (newValue.length === 10 && this._patternMatched(newValue)) {
          this.hasError = false;
          this.dateChange.emit(parse(newValue, this.dateFormat, new Date()));
        }
      }
    } else {
      const { err, value } = this.validateDate(evt.target.value);

      if (err) {
        this.hasError = true;
        this.tooltip = err;
      } else if (value) {
        this.hasError = false;
        this.dateChange.emit(parse(value, this.dateFormat, new Date()));
      }
    }
  }

  validateDate(value: string) {
    if (value.length !== 10) {
      return { err: this.errorTexts.default };
    } else if (this._patternMatched(value)) {
      const err = this._minMaxError(parse(value, this.dateFormat, new Date()));

      if (err) return { err };
      else return { value };
    } else {
      const errType = GetDateErrorType(value, this.dateFormat);
      return { err: this.errorTexts[errType] };
    }
  }

  private _patternMatched(value: string) {
    return DateValidationPattern(this.dateFormat).test(value);
  }

  private _minMaxError(value: Date) {
    if (this.maxDate && +value > +this.maxDate) {
      const nextDay = add(this.maxDate, { days: 1 });
      return this.errorTexts.maxDate.replace('{{date}}', format(nextDay, 'dd MMM, yyyy'));
    } else if (this.minDate && +value < +this.minDate) {
      const prevDay = add(this.maxDate, { days: -1 });
      return this.errorTexts.minDate.replace('{{date}}', format(prevDay, 'dd MMM, yyyy'));
    }
  }

  constructor(private translate: TranslateService) {}

  ngOnChanges(changes: SimpleChanges) {
    if (!!changes.date) {
      if (isValid(changes.date.currentValue)) {
        const { err, value } = this.validateDate(format(changes.date.currentValue, this.dateFormat));
        if (value) {
          this.tooltip = format(changes.date.currentValue, 'dd MMM, yyyy');
          this.hasError = false;
        } else {
          this.hasError = true;
          this.tooltip = err;
        }
      }
    }
    // if (changes.selected?.currentValue && !!this.inputElement?.nativeElement) {
    //   this.inputElement.nativeElement.focus();
    // }
  }

  ngOnInit() {
    this.errorTexts.default = this.errorTexts.default.replace('{{format}}', this.dateFormat);
  }
}
